// Formatage partagé par les surfaces.
import type { CSSProperties } from 'react'
import { mot } from './vocabulaire'
import type { Formule } from './formule'
import type { Lang } from './lang'
import { LOCALE_UI } from '@ai-job-fit/engine/langues'

/** Les datetime SQLite sont en UTC sans suffixe — on le rétablit, sinon le
 *  navigateur les lit en heure locale et tout est décalé de deux heures. */
export function parseDate(s: string | null | undefined): Date | null {
  if (!s) return null
  return new Date(s.includes('T') ? s : s.replace(' ', 'T') + 'Z')
}

/**
 * Langue courante des dates, en registre GLOBAL plutôt qu'en paramètre de
 * chaque fonction ci-dessous.
 *
 * POURQUOI. `relTime`/`fullDate`/`dateHeure` sont appelées à des dizaines
 * d'endroits (chaque ligne de chaque liste). Leur faire recevoir `lang` en
 * plus aurait démultiplié le balayage du chantier i18n sans rien changer au
 * résultat, pour un gain de rigueur nul : la console est mono-utilisateur, un
 * seul onglet affiche une seule langue à la fois, donc un registre global ne
 * peut pas mélanger les langues de deux visiteurs comme le ferait un serveur.
 *
 * `App.tsx` l'appelle en tête de son propre rendu (PAS dans un `useEffect`,
 * qui s'exécuterait un rendu trop tard et laisserait les dates de la
 * première passe dans l'ancienne langue) : au moment où les composants
 * enfants s'exécutent, le registre est déjà à jour. Un composant qui
 * n'appellerait QUE ces fonctions sans lire `useLang()`/`useT()`/`useMots()`
 * par ailleurs ne se re-rendrait pas tout seul sur une bascule ; en pratique
 * tous les écrans qui affichent une date affichent aussi du texte traduit
 * par ailleurs, donc se re-rendent déjà pour cette raison.
 */
let langueDates: Lang = 'fr'
export function definirLangueDates(l: Lang): void { langueDates = l }
const locale = () => LOCALE_UI[langueDates]

/** Les mots des dates, par langue. `{n}` est le nombre. */
const MOTS_DATES: Record<Lang, {
  minutes: string; heures: string; hier: string; jours: string
  aujourdhui: string; hierA: string; nonRenseignee: string; expiree: string; joursRestants: string
  heure: (h: string, m: string) => string
}> = {
  fr: {
    minutes: 'il y a {n} min', heures: 'il y a {n} h', hier: 'hier', jours: '{n} j',
    aujourdhui: "Aujourd'hui", hierA: 'Hier', nonRenseignee: 'non renseignée', expiree: 'expirée', joursRestants: '{n} j',
    // Construit à la main plutôt que `toLocaleTimeString` : l'ICU de fr-BE rend
    // « 14:20 » (deux-points), pas la convention française « 14h20 » attendue
    // partout ailleurs dans ce dépôt (journal, aperçu du CV).
    heure: (h, m) => `${h}h${m}`,
  },
  en: {
    minutes: '{n} min ago', heures: '{n} h ago', hier: 'yesterday', jours: '{n} d',
    aujourdhui: 'Today', hierA: 'Yesterday', nonRenseignee: 'not specified', expiree: 'expired', joursRestants: '{n}d',
    heure: (h, m) => `${h}:${m}`,
  },
  nl: {
    minutes: '{n} min geleden', heures: '{n} u geleden', hier: 'gisteren', jours: '{n} d',
    aujourdhui: 'Vandaag', hierA: 'Gisteren', nonRenseignee: 'niet vermeld', expiree: 'verlopen', joursRestants: '{n} d',
    heure: (h, m) => `${h}:${m}`,
  },
  it: {
    minutes: '{n} min fa', heures: '{n} h fa', hier: 'ieri', jours: '{n} g',
    aujourdhui: 'Oggi', hierA: 'Ieri', nonRenseignee: 'non indicata', expiree: 'scaduta', joursRestants: '{n} g',
    heure: (h, m) => `${h}:${m}`,
  },
  es: {
    minutes: 'hace {n} min', heures: 'hace {n} h', hier: 'ayer', jours: '{n} d',
    aujourdhui: 'Hoy', hierA: 'Ayer', nonRenseignee: 'sin indicar', expiree: 'caducada', joursRestants: '{n} d',
    heure: (h, m) => `${h}:${m}`,
  },
}
const mots = () => MOTS_DATES[langueDates]
const avecN = (gabarit: string, n: number) => gabarit.replace('{n}', String(n))

export function relTime(s: string | null | undefined): string {
  const d = parseDate(s)
  if (!d) return '—'
  const m = mots()
  const mins = Math.round((Date.now() - d.getTime()) / 60000)
  if (mins < 60) return avecN(m.minutes, Math.max(1, mins))
  const h = Math.round(mins / 60)
  if (h < 24) return avecN(m.heures, h)
  const days = Math.round(h / 24)
  if (days === 1) return m.hier
  if (days < 7) return avecN(m.jours, days)
  return d.toLocaleDateString(locale(), { day: 'numeric', month: 'short' })
}

/** « 15 oct. » : un jour à venir, dans une phrase (« Relancer le 15 oct. »). */
export function jourCourt(d: Date): string {
  return d.toLocaleDateString(locale(), { day: 'numeric', month: 'short' })
}

export function fullDate(s: string | null | undefined): string {
  const d = parseDate(s)
  return d ? d.toLocaleDateString(locale(), { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
}

/**
 * « Aujourd'hui, 09h44 » plutôt que huit fois la même date complète dans un
 * journal : sur une session de travail, tout est souvent arrivé le même
 * jour, et c'est l'HEURE qui distingue deux événements, pas la date. La date
 * entière ne revient que si ce n'est ni aujourd'hui ni hier, où elle redevient
 * l'information utile. Demandé le 25 août 2026.
 */
export function dateHeure(s: string | null | undefined): string {
  const d = parseDate(s)
  if (!d) return '—'
  const m = mots()
  const deuxChiffres = (n: number) => String(n).padStart(2, '0')
  const heure = m.heure(deuxChiffres(d.getHours()), deuxChiffres(d.getMinutes()))
  const maintenant = new Date()
  const jours = Math.floor((Date.UTC(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate())
    - Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000)
  if (jours === 0) return `${m.aujourdhui}, ${heure}`
  if (jours === 1) return `${m.hierA}, ${heure}`
  return `${d.toLocaleDateString(locale(), { day: 'numeric', month: 'short' })}, ${heure}`
}

/**
 * Temps restant avant une échéance de mission, PAS son ancienneté : c'est ce
 * que missions-consultance.md §4.2 établit — `createdAt` ne dit rien de
 * l'urgence, seule la date limite compte. `urgent` marque sous 48 h (la moitié
 * des demandes observées le 15/08 y étaient), pour un style distinct.
 */
export function tempsRestant(s: string | null | undefined): { texte: string; urgent: boolean; expiree: boolean } {
  const d = parseDate(s)
  const m = mots()
  if (!d) return { texte: m.nonRenseignee, urgent: false, expiree: false }
  const ms = d.getTime() - Date.now()
  if (ms <= 0) return { texte: m.expiree, urgent: true, expiree: true }
  const heures = Math.round(ms / 3600000)
  if (heures < 48) return { texte: `${Math.max(1, heures)} h`, urgent: true, expiree: false }
  const jours = Math.round(heures / 24)
  return { texte: avecN(m.joursRestants, jours), urgent: false, expiree: false }
}

/**
 * La teinte (0 à 360) d'un monogramme, stable pour un même nom : Proximus a
 * toujours la même couleur, d'une liste à l'autre et d'un jour à l'autre.
 * Rendue en style inline (`--h`) ; les couleurs elles-mêmes se calculent dans
 * app.css, à partir du thème (bloc « Teintes »).
 */
export function teinte(name: string | null | undefined): CSSProperties {
  let h = 0
  for (const c of String(name ?? '').toLowerCase()) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return { '--h': h % 360 } as CSSProperties
}

export function initials(name: string | null | undefined): string {
  // Le premier caractère ALPHANUMÉRIQUE de chaque mot : « jobs.lu
  // (Luxembourg) » donnait « J( » (issue #77).
  return String(name ?? '?')
    .split(/\s+/)
    .map((w) => w.match(/[\p{L}\p{N}]/u)?.[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'
}

// Les paliers de couleur d'un score viennent du paquet partagé. La console
// avait les siens ('hi' ≥80, 'mid' ≥65, 'lo' sinon) là où le widget utilise
// 'good' ≥70 / 'mid' ≥40 / 'low' : le même 72/100 s'affichait vert dans
// l'extension et orange ici. Réexporté plutôt qu'importé partout, pour ne pas
// toucher les appels existants.
export { scoreTone, type ScoreTone } from '@ai-job-fit/ui'

/** Un particulier n'est ni « sur le banc » ni « en mission » : ces mots
 *  décrivent la position d'un salarié dans une société de consultance. */
export const CONSULTANT_STATUS_RADAR = {
  fr: {
    available: { label: 'En recherche', cls: 'st-ok', aide: 'Vous cherchez un poste' },
    placed: { label: 'En poste', cls: 'st-placed', aide: 'Vous ne cherchez plus' },
    archived: { label: 'Archivé', cls: 'st-arch' },
  },
  en: {
    available: { label: 'Looking', cls: 'st-ok', aide: 'You are looking for a role' },
    placed: { label: 'Employed', cls: 'st-placed', aide: 'You are no longer looking' },
    archived: { label: 'Archived', cls: 'st-arch' },
  },
  nl: {
    available: { label: 'Op zoek', cls: 'st-ok', aide: 'U zoekt een job' },
    placed: { label: 'Aan het werk', cls: 'st-placed', aide: 'U zoekt niet meer' },
    archived: { label: 'Gearchiveerd', cls: 'st-arch' },
  },
  it: {
    available: { label: 'In cerca', cls: 'st-ok', aide: 'Stai cercando un lavoro' },
    placed: { label: 'Occupato', cls: 'st-placed', aide: 'Non cerchi più' },
    archived: { label: 'Archiviato', cls: 'st-arch' },
  },
  es: {
    available: { label: 'Buscando', cls: 'st-ok', aide: 'Buscas un puesto' },
    placed: { label: 'Con empleo', cls: 'st-placed', aide: 'Ya no buscas' },
    archived: { label: 'Archivado', cls: 'st-arch' },
  },
} as const

export const CONSULTANT_STATUS_CONSOLE = {
  fr: {
    available: { label: 'Disponible', cls: 'act' },
    placed: { label: 'En mission', cls: 'placed' },
    archived: { label: 'Archivé', cls: 'arc' },
  },
  en: {
    available: { label: 'Available', cls: 'act' },
    placed: { label: 'On a mission', cls: 'placed' },
    archived: { label: 'Archived', cls: 'arc' },
  },
  nl: {
    available: { label: 'Beschikbaar', cls: 'act' },
    placed: { label: 'Op opdracht', cls: 'placed' },
    archived: { label: 'Gearchiveerd', cls: 'arc' },
  },
  it: {
    available: { label: 'Disponibile', cls: 'act' },
    placed: { label: 'In missione', cls: 'placed' },
    archived: { label: 'Archiviato', cls: 'arc' },
  },
  es: {
    available: { label: 'Disponible', cls: 'act' },
    placed: { label: 'En misión', cls: 'placed' },
    archived: { label: 'Archivado', cls: 'arc' },
  },
} as const

export function consultantStatus(
  formule: 'console' | 'radar',
  lang: Lang = 'fr'
): Record<string, { label: string; cls: string; aide?: string }> {
  return formule === 'radar' ? CONSULTANT_STATUS_RADAR[lang] : CONSULTANT_STATUS_CONSOLE[lang]
}

/** Compat : formule console, français. Un appelant qui veut l'anglais ou
 *  Radar passe par `consultantStatus(formule, lang)`. */
export const CONSULTANT_STATUS = CONSULTANT_STATUS_CONSOLE.fr

// Le statut de TRI de l'offre. « Dossier généré » n'y figure plus, et c'est le
// point de tout le remaniement : un document produit par l'IA n'est pas une
// décision, il ne peut donc plus décrire l'état d'une offre.
//
// Les mots viennent du vocabulaire (`etat.*`), qui dépend de la formule : un
// statut porte le MÊME nom sur tous les écrans d'un produit (issue #77).
export function offerStatus(formule: Formule, lang: Lang = 'fr'): Record<string, string> {
  return {
    todo: mot('etat.todo', formule, lang),
    later: mot('etat.later', formule, lang),
    skip: mot('etat.skip', formule, lang),
  }
}

/** Compat : console, français. */
export const OFFER_STATUS: Record<string, string> = offerStatus('console')

// L'avancement d'une candidature. L'ordre de ce dictionnaire EST l'ordre du
// pipeline affiché : les colonnes du suivi et le sélecteur de la fiche offre
// le parcourent tel quel, pour qu'il n'existe qu'une seule source à cet ordre.
export interface StatutCandidature {
  label: string
  cls: string
  aide: string
}

/**
 * MÊME pipeline, DEUX récits.
 *
 * Les étapes sont identiques parce que la réalité l'est : on envoie, on
 * relance, on est reçu, ça se conclut. Ce qui change est QUI fait l'action et
 * DE QUI on parle. Un business manager propose quelqu'un à un client et le
 * place ; un particulier postule à un employeur et est embauché. « Placé » et
 * « Mission signée » décrivent une transaction dont le particulier n'est pas
 * partie prenante, ils sonnent faux même quand ils désignent le bon état.
 *
 * Les CLÉS ne changent pas : ce sont des états en base, partagés, et les
 * traduire des deux côtés créerait deux vocabulaires de données pour un seul
 * pipeline.
 */
const CANDIDATURE_CONSOLE_FR: Record<string, StatutCandidature> = {
  a_envoyer: { label: 'À envoyer', cls: 'k-todo', aide: 'Pressenti, rien n’est parti' },
  envoye: { label: 'Envoyé', cls: 'k-sent', aide: 'Proposé au client' },
  relance: { label: 'Relancé', cls: 'k-ping', aide: 'Sans réponse, on a rappelé' },
  entretien: { label: 'Entretien', cls: 'k-meet', aide: 'Le client veut le voir' },
  place: { label: 'Placé', cls: 'k-won', aide: 'Mission signée' },
  refuse: { label: 'Refusé', cls: 'k-lost', aide: 'Le client a dit non' },
}

const CANDIDATURE_CONSOLE_EN: Record<string, StatutCandidature> = {
  a_envoyer: { label: 'To send', cls: 'k-todo', aide: 'Shortlisted, nothing sent yet' },
  envoye: { label: 'Sent', cls: 'k-sent', aide: 'Put forward to the client' },
  relance: { label: 'Chased', cls: 'k-ping', aide: 'No reply, we followed up' },
  entretien: { label: 'Interview', cls: 'k-meet', aide: 'The client wants to meet them' },
  place: { label: 'Placed', cls: 'k-won', aide: 'Mission signed' },
  refuse: { label: 'Turned down', cls: 'k-lost', aide: 'The client said no' },
}

const CANDIDATURE_CONSOLE_NL: Record<string, StatutCandidature> = {
  a_envoyer: { label: 'Te versturen', cls: 'k-todo', aide: 'Voorgedragen, nog niets verstuurd' },
  envoye: { label: 'Verstuurd', cls: 'k-sent', aide: 'Voorgesteld aan de klant' },
  relance: { label: 'Opgevolgd', cls: 'k-ping', aide: 'Geen antwoord, we hebben opgevolgd' },
  entretien: { label: 'Gesprek', cls: 'k-meet', aide: 'De klant wil kennismaken' },
  place: { label: 'Geplaatst', cls: 'k-won', aide: 'Opdracht getekend' },
  refuse: { label: 'Afgewezen', cls: 'k-lost', aide: 'De klant zei nee' },
}

const CANDIDATURE_RADAR_FR: Record<string, StatutCandidature> = {
  a_envoyer: { label: 'À envoyer', cls: 'k-todo', aide: 'Repérée, rien n’est parti' },
  envoye: { label: 'Envoyée', cls: 'k-sent', aide: 'Candidature envoyée à l’employeur' },
  relance: { label: 'Relancée', cls: 'k-ping', aide: 'Sans réponse, vous avez relancé' },
  entretien: { label: 'Entretien', cls: 'k-meet', aide: 'L’employeur veut vous rencontrer' },
  place: { label: 'Embauché', cls: 'k-won', aide: 'Contrat signé' },
  refuse: { label: 'Refusée', cls: 'k-lost', aide: 'Réponse négative' },
}

const CANDIDATURE_RADAR_EN: Record<string, StatutCandidature> = {
  a_envoyer: { label: 'To send', cls: 'k-todo', aide: 'Spotted, nothing sent yet' },
  envoye: { label: 'Sent', cls: 'k-sent', aide: 'Application sent to the employer' },
  relance: { label: 'Chased', cls: 'k-ping', aide: 'No reply, you followed up' },
  entretien: { label: 'Interview', cls: 'k-meet', aide: 'The employer wants to meet you' },
  place: { label: 'Hired', cls: 'k-won', aide: 'Contract signed' },
  refuse: { label: 'Turned down', cls: 'k-lost', aide: 'Negative reply' },
}

const CANDIDATURE_RADAR_NL: Record<string, StatutCandidature> = {
  a_envoyer: { label: 'Te versturen', cls: 'k-todo', aide: 'Gevonden, nog niets verstuurd' },
  envoye: { label: 'Verstuurd', cls: 'k-sent', aide: 'Sollicitatie naar de werkgever verstuurd' },
  relance: { label: 'Opgevolgd', cls: 'k-ping', aide: 'Geen antwoord, u hebt opgevolgd' },
  entretien: { label: 'Gesprek', cls: 'k-meet', aide: 'De werkgever wil u ontmoeten' },
  place: { label: 'Aangenomen', cls: 'k-won', aide: 'Contract getekend' },
  refuse: { label: 'Afgewezen', cls: 'k-lost', aide: 'Negatief antwoord' },
}

const CANDIDATURE_CONSOLE_IT: Record<string, StatutCandidature> = {
  a_envoyer: { label: 'Da inviare', cls: 'k-todo', aide: 'Individuato, niente è partito' },
  envoye: { label: 'Inviato', cls: 'k-sent', aide: 'Proposto al cliente' },
  relance: { label: 'Sollecitato', cls: 'k-ping', aide: 'Nessuna risposta, abbiamo sollecitato' },
  entretien: { label: 'Colloquio', cls: 'k-meet', aide: 'Il cliente vuole incontrarlo' },
  place: { label: 'Inserito', cls: 'k-won', aide: 'Missione firmata' },
  refuse: { label: 'Rifiutato', cls: 'k-lost', aide: 'Il cliente ha detto no' },
}

const CANDIDATURE_RADAR_IT: Record<string, StatutCandidature> = {
  a_envoyer: { label: 'Da inviare', cls: 'k-todo', aide: 'Individuata, niente è partito' },
  envoye: { label: 'Inviata', cls: 'k-sent', aide: 'Candidatura inviata al datore di lavoro' },
  relance: { label: 'Sollecitata', cls: 'k-ping', aide: 'Nessuna risposta, hai sollecitato' },
  entretien: { label: 'Colloquio', cls: 'k-meet', aide: 'Il datore di lavoro vuole incontrarti' },
  place: { label: 'Assunto', cls: 'k-won', aide: 'Contratto firmato' },
  refuse: { label: 'Rifiutata', cls: 'k-lost', aide: 'Risposta negativa' },
}

const CANDIDATURE_CONSOLE_ES: Record<string, StatutCandidature> = {
  a_envoyer: { label: 'Por enviar', cls: 'k-todo', aide: 'Preseleccionado, no ha salido nada' },
  envoye: { label: 'Enviado', cls: 'k-sent', aide: 'Propuesto al cliente' },
  relance: { label: 'Seguimiento', cls: 'k-ping', aide: 'Sin respuesta, hemos insistido' },
  entretien: { label: 'Entrevista', cls: 'k-meet', aide: 'El cliente quiere conocerlo' },
  place: { label: 'Colocado', cls: 'k-won', aide: 'Misión firmada' },
  refuse: { label: 'Rechazado', cls: 'k-lost', aide: 'El cliente ha dicho que no' },
}

const CANDIDATURE_RADAR_ES: Record<string, StatutCandidature> = {
  a_envoyer: { label: 'Por enviar', cls: 'k-todo', aide: 'Detectada, no ha salido nada' },
  envoye: { label: 'Enviada', cls: 'k-sent', aide: 'Candidatura enviada al empleador' },
  relance: { label: 'Seguimiento', cls: 'k-ping', aide: 'Sin respuesta, has insistido' },
  entretien: { label: 'Entrevista', cls: 'k-meet', aide: 'El empleador quiere conocerte' },
  place: { label: 'Contratado', cls: 'k-won', aide: 'Contrato firmado' },
  refuse: { label: 'Rechazada', cls: 'k-lost', aide: 'Respuesta negativa' },
}

const CANDIDATURE: Record<'console' | 'radar', Record<Lang, Record<string, StatutCandidature>>> = {
  console: { fr: CANDIDATURE_CONSOLE_FR, en: CANDIDATURE_CONSOLE_EN, nl: CANDIDATURE_CONSOLE_NL, it: CANDIDATURE_CONSOLE_IT, es: CANDIDATURE_CONSOLE_ES },
  radar: { fr: CANDIDATURE_RADAR_FR, en: CANDIDATURE_RADAR_EN, nl: CANDIDATURE_RADAR_NL, it: CANDIDATURE_RADAR_IT, es: CANDIDATURE_RADAR_ES },
}

export function candidatureStatus(
  formule: 'console' | 'radar',
  lang: Lang = 'fr'
): Record<string, StatutCandidature> {
  return CANDIDATURE[formule][lang]
}

/** Compat : l'ORDRE du pipeline et les clés, indépendants de la formule ET de
 *  la langue (ce sont des états en base, jamais affichés bruts). */
export const CANDIDATURE_STATUS = CANDIDATURE_CONSOLE_FR

/** Une candidature close ne demande plus rien : elle sort du pipeline actif. */
export const estClose = (s: string): boolean => s === 'place' || s === 'refuse'

// Le journal est la source de vérité des statuts : un type sans libellé
// s'affiche brut (`offer.detected`) au milieu de phrases françaises. La liste
// couvre donc TOUS les types réellement présents en base, pas seulement ceux
// qu'un écran affichait au moment de l'écrire.
const EVENT_CONSOLE_FR: Record<string, string> = {
  'offer.detected': 'Détectée sur la source',
  'dossier.bullet_reverted': 'Une phrase du dossier remise à l’original',
  'source.added': 'Source ajoutée à la veille',
  'source_request.created': "Activation d'un site demandée",
  'source_request.notified': 'Demande transmise par e-mail',
  'source_request.activated': 'Site demandé mis en veille',
  'source_request.activation_notified': 'Mise en ligne annoncée au demandeur',
  'source_request.refused': 'Demande refusée',
  'source_request.refusal_notified': 'Refus annoncé au demandeur',
  'source_request.renotified': 'Accusé de réception relancé',
  'source.subscribed': 'Site suivi par un compte',
  'source.unsubscribed': 'Site retiré du suivi par un compte',
  'source.filtre': 'Filtre d’un site suivi changé',
  'source.elargie': 'Filtre retiré de la config d’un site, reporté sur ses abonnés',
  'extension.token_created': "Jeton d'extension fabriqué",
  'feedback.sent': 'Retour envoyé par le compte',
  'courriers.coupes': 'Mails de Radar coupés',
  'courriers.rythme': 'Rythme des mails changé',
  'courriers.reactives': 'Mails de Radar réactivés',
  'courriers.pause': 'Mails de Radar en pause',
  'courriers.pause_levee': 'Pause des mails levée',
  'compte.supprime': 'Compte supprimé',
  'essai.rappel': 'Rappel de fin d’essai envoyé',
  'courriers.regions': 'Régions des mails changées',
  'courriers.reglages': 'Réglages des mails changés',
  'courriers.exclusions': 'Critères qui excluent changés',
  'suggestion.ignoree': 'Suggestion écartée',
  'courriers.connexion': 'Connexion par le lien d’un mail',
  'candidature.relance_redigee': 'Relance rédigée',
  'employeurs.pont_refuse': 'Suggestion de suivre un employeur déclinée',
  'courriers.adresse': 'Adresse de réception des mails changée',
  'offer.rappel': 'Offre « plus tard » rappelée par mail',
  'offer.score_failed': "Évaluation impossible, à reprendre au run suivant",
  'source.updated': 'Source corrigée',
  'source.logo': 'Logo changé',
  'employeur.logo': "Logo d'employeur changé",
  'source.restored': 'Source remise en veille',
  'settings.changed': 'Réglage modifié',
  'run.completed': 'Run nocturne terminé',
  'consultant.imported': 'Importé dans le vivier',
  'consultant.updated': 'Profil mis à jour (re-import CV)',
  'consultant.mise_en_page': 'Mise en page tirée du CV',
  'consultant.mise_en_page_refusee': 'Mise en page refusée',
  'consultant.edited': 'CV retouché à la main',
  'consultant.archived': 'Archivé, historique conservé',
  'consultant.reset': 'Compte remis à zéro, profil archivé',
  'consultant.caught_up': 'Évalué sur les offres déjà en base',
  'consultant.unarchived': 'Réactivé dans le vivier',
  'consultant.placed': 'Placé en mission',
  'consultant.released': 'Revenu sur le banc',
  'dossier.generated': 'Dossier généré',
  'offer.status': 'Décision de tri',
  'candidature.created': 'Profil proposé sur cette offre',
  'candidature.status': 'Candidature : changement d’étape',
  'candidature.archived': 'Candidature retirée',
  'candidature.restored': 'Candidature remise en piste',
  'offer.scored': 'Évaluée automatiquement',
  'offer.scored_on_demand': 'Profil évalué à la demande',
  'offer.read': 'Lue',
  'offer.expired': 'Expirée, disparue du site',
  'offer.relisted': 'Republiée par le site',
  'offer.categorised': 'Rangée dans une catégorie de métier',
  'consultant.categorised': 'Catégories de métier définies',
  'score.feedback': 'Avis donné sur un score',
  'consultant.enrichment_facts_added': 'Réponses aux questions d’écart enregistrées',
  'dossier.fragment_revised': 'Passage retouché par l’IA',
  'dossier.edited': 'CV corrigé à la main',
  'dossier.letter_generated': 'Lettre de motivation générée',
  'dossier.letter_refined': 'Lettre de motivation ajustée',
  'dossier.letter_kept': 'Lettre gardée pour la nouvelle version du CV',
  'dossier.letter_angles': 'Angle de la lettre choisi',
  'candidature.entretien_prepare': 'Entretien préparé',
  'offre.salaire_estime': 'Salaire estimé',
  'source.error': 'Erreur de lecture de la source',
  'source.fallback': 'Repli utilisé pour lire la source',
  'source.archivage_suspendu': 'Archivage suspendu : trop d’offres disparues d’un coup',
  'source.archivage_force': 'Archivage forcé après vérification',
  'offer.description_missing': 'Description absente ou trop courte',
  'offer.added': 'Ajoutée manuellement',
}

const EVENT_CONSOLE_EN: Record<string, string> = {
  'offer.detected': 'Detected on the source',
  'dossier.bullet_reverted': 'A file bullet reverted to the original',
  'source.added': 'Source added to the watch',
  'source_request.created': 'Activation of a site requested',
  'source_request.notified': 'Request sent by email',
  'source_request.activated': 'Requested site is now watched',
  'source_request.activation_notified': 'Requester told it is live',
  'source_request.refused': 'Request refused',
  'source_request.refusal_notified': 'Requester told about the refusal',
  'source_request.renotified': 'Receipt resent',
  'source.subscribed': 'Site followed by an account',
  'source.unsubscribed': 'Site unfollowed by an account',
  'source.filtre': 'Filter of a followed site changed',
  'source.elargie': 'Filter moved from a site config to its followers',
  'extension.token_created': 'Extension token created',
  'feedback.sent': 'Feedback sent by the account',
  'courriers.coupes': 'Radar emails turned off',
  'courriers.rythme': 'Email pace changed',
  'courriers.reactives': 'Radar emails turned back on',
  'courriers.pause': 'Radar emails paused',
  'courriers.pause_levee': 'Email break ended',
  'compte.supprime': 'Account deleted',
  'essai.rappel': 'Trial ending reminder sent',
  'courriers.regions': 'Email regions changed',
  'courriers.reglages': 'Email settings changed',
  'courriers.exclusions': 'Exclusion criteria changed',
  'suggestion.ignoree': 'Suggestion dismissed',
  'courriers.connexion': 'Signed in from an email link',
  'candidature.relance_redigee': 'Follow-up drafted',
  'employeurs.pont_refuse': 'Suggestion to follow an employer declined',
  'courriers.adresse': 'Email delivery address changed',
  'offer.rappel': '“Later” job reminded by email',
  'offer.score_failed': 'Scoring failed, to be retried on the next run',
  'source.updated': 'Source corrected',
  'source.logo': 'Logo changed',
  'employeur.logo': 'Employer logo changed',
  'source.restored': 'Source put back in the watch',
  'settings.changed': 'Setting changed',
  'run.completed': 'Nightly run completed',
  'consultant.imported': 'Imported into the bench',
  'consultant.updated': 'Profile updated (CV re-imported)',
  'consultant.mise_en_page': 'Layout taken from the CV',
  'consultant.mise_en_page_refusee': 'Layout rejected',
  'consultant.edited': 'CV edited by hand',
  'consultant.archived': 'Archived, history kept',
  'consultant.reset': 'Account reset, profile archived',
  'consultant.caught_up': 'Scored against the postings already stored',
  'consultant.unarchived': 'Reactivated in the bench',
  'consultant.placed': 'Placed on a mission',
  'consultant.released': 'Back on the bench',
  'dossier.generated': 'File generated',
  'offer.status': 'Sorting decision',
  'candidature.created': 'Profile put forward on this posting',
  'candidature.status': 'Application: stage changed',
  'candidature.archived': 'Application removed',
  'candidature.restored': 'Application put back in play',
  'offer.scored': 'Automatically evaluated',
  'offer.scored_on_demand': 'Profile evaluated on demand',
  'offer.read': 'Read',
  'offer.expired': 'Expired, removed from the site',
  'offer.relisted': 'Relisted by the site',
  'offer.categorised': 'Filed under an occupation category',
  'consultant.categorised': 'Occupation categories set',
  'score.feedback': 'Feedback given on a score',
  'consultant.enrichment_facts_added': 'Answers to gap questions saved',
  'dossier.fragment_revised': 'Passage reworked by the AI',
  'dossier.edited': 'CV edited by hand',
  'dossier.letter_generated': 'Cover letter generated',
  'dossier.letter_refined': 'Cover letter adjusted',
  'dossier.letter_kept': 'Letter kept for the new CV version',
  'dossier.letter_angles': 'Cover letter angle chosen',
  'candidature.entretien_prepare': 'Interview prepared',
  'offre.salaire_estime': 'Salary estimated',
  'source.error': 'Error reading the source',
  'source.fallback': 'Fallback used to read the source',
  'source.archivage_suspendu': 'Archiving suspended: too many offers vanished at once',
  'source.archivage_force': 'Archiving forced after checking',
  'offer.description_missing': 'Description missing or too short',
  'offer.added': 'Added manually',
}

const EVENT_CONSOLE_NL: Record<string, string> = {
  'offer.detected': 'Gevonden op de bron',
  'dossier.bullet_reverted': 'Een zin uit het dossier teruggezet naar het origineel',
  'source.added': 'Bron toegevoegd aan de opvolging',
  'source_request.created': 'Activering van een site aangevraagd',
  'source_request.notified': 'Aanvraag per e-mail doorgestuurd',
  'source_request.activated': 'Aangevraagde site wordt nu opgevolgd',
  'source_request.activation_notified': 'Aanvrager verwittigd dat de site online is',
  'source_request.refused': 'Aanvraag geweigerd',
  'source_request.refusal_notified': 'Aanvrager verwittigd van de weigering',
  'source_request.renotified': 'Ontvangstbevestiging opnieuw verstuurd',
  'source.subscribed': 'Site gevolgd door een account',
  'source.unsubscribed': 'Site niet meer gevolgd door een account',
  'source.filtre': 'Filter van een gevolgde site gewijzigd',
  'source.elargie': 'Filter uit de configuratie van een site naar de volgers verplaatst',
  'extension.token_created': 'Extensiesleutel aangemaakt',
  'feedback.sent': 'Feedback verstuurd door het account',
  'courriers.coupes': 'Mails van Radar uitgeschakeld',
  'courriers.rythme': 'Mailritme gewijzigd',
  'courriers.reactives': 'Mails van Radar opnieuw ingeschakeld',
  'courriers.pause': 'Mails van Radar gepauzeerd',
  'courriers.pause_levee': 'Mailpauze beëindigd',
  'compte.supprime': 'Account verwijderd',
  'essai.rappel': 'Herinnering einde proefperiode verstuurd',
  'courriers.regions': 'Regio’s van de mails gewijzigd',
  'courriers.reglages': 'Mailinstellingen gewijzigd',
  'courriers.exclusions': 'Uitsluitingscriteria gewijzigd',
  'suggestion.ignoree': 'Suggestie genegeerd',
  'courriers.connexion': 'Aangemeld via de link in een mail',
  'candidature.relance_redigee': 'Opvolgmail opgesteld',
  'employeurs.pont_refuse': 'Voorstel om een werkgever te volgen afgewezen',
  'courriers.adresse': 'Ontvangstadres van de mails gewijzigd',
  'offer.rappel': 'Vacature “later” per mail herinnerd',
  'offer.score_failed': 'Beoordeling mislukt, opnieuw bij de volgende run',
  'source.updated': 'Bron verbeterd',
  'source.logo': 'Logo gewijzigd',
  'employeur.logo': 'Logo van werkgever gewijzigd',
  'source.restored': 'Bron opnieuw opgevolgd',
  'settings.changed': 'Instelling gewijzigd',
  'run.completed': 'Nachtelijke run afgerond',
  'consultant.imported': 'Geïmporteerd in de pool',
  'consultant.updated': 'Profiel bijgewerkt (cv opnieuw geïmporteerd)',
  'consultant.mise_en_page': 'Opmaak uit het cv gehaald',
  'consultant.mise_en_page_refusee': 'Opmaak geweigerd',
  'consultant.edited': 'Cv met de hand aangepast',
  'consultant.archived': 'Gearchiveerd, geschiedenis bewaard',
  'consultant.reset': 'Account op nul gezet, profiel gearchiveerd',
  'consultant.caught_up': 'Beoordeeld op de vacatures die al in de databank stonden',
  'consultant.unarchived': 'Opnieuw actief in de pool',
  'consultant.placed': 'Op opdracht geplaatst',
  'consultant.released': 'Terug beschikbaar',
  'dossier.generated': 'Dossier aangemaakt',
  'offer.status': 'Sorteerbeslissing',
  'candidature.created': 'Profiel voorgesteld voor deze vacature',
  'candidature.status': 'Sollicitatie: andere stap',
  'candidature.archived': 'Sollicitatie verwijderd',
  'candidature.restored': 'Sollicitatie opnieuw actief',
  'offer.scored': 'Automatisch beoordeeld',
  'offer.scored_on_demand': 'Profiel op aanvraag beoordeeld',
  'offer.read': 'Gelezen',
  'offer.expired': 'Verlopen, verdwenen van de site',
  'offer.relisted': 'Opnieuw gepubliceerd door de site',
  'offer.categorised': 'Ingedeeld in een beroepscategorie',
  'consultant.categorised': 'Beroepscategorieën vastgelegd',
  'score.feedback': 'Mening gegeven over een score',
  'consultant.enrichment_facts_added': 'Antwoorden op de vragen over ontbrekende punten bewaard',
  'dossier.fragment_revised': 'Passage herwerkt door de AI',
  'dossier.edited': 'Cv met de hand verbeterd',
  'dossier.letter_generated': 'Motivatiebrief opgesteld',
  'dossier.letter_refined': 'Motivatiebrief bijgestuurd',
  'dossier.letter_kept': 'Brief behouden voor de nieuwe versie van het cv',
  'dossier.letter_angles': 'Invalshoek van de brief gekozen',
  'candidature.entretien_prepare': 'Gesprek voorbereid',
  'offre.salaire_estime': 'Salaris geschat',
  'source.error': 'Fout bij het lezen van de bron',
  'source.fallback': 'Alternatief gebruikt om de bron te lezen',
  'source.archivage_suspendu': 'Archivering opgeschort: te veel vacatures tegelijk verdwenen',
  'source.archivage_force': 'Archivering geforceerd na controle',
  'offer.description_missing': 'Beschrijving ontbreekt of is te kort',
  'offer.added': 'Met de hand toegevoegd',
}

/** Ce qui se dit autrement pour un particulier. Les clés absentes retombent
 *  sur le dictionnaire console : inutile de tout recopier pour changer six
 *  phrases, et une entrée oubliée reste lisible au lieu de disparaître. */
const EVENT_RADAR_FR: Record<string, string> = {
  'consultant.imported': 'Profil créé depuis votre CV',
  'consultant.updated': 'Profil mis à jour depuis votre CV',
  'consultant.mise_en_page': 'Mise en page tirée de votre CV',
  'consultant.mise_en_page_refusee': 'Mise en page non tirée de votre CV',
  'consultant.edited': 'CV retouché à la main',
  'consultant.archived': 'Profil archivé',
  'consultant.unarchived': 'Profil réactivé',
  'consultant.placed': 'En poste',
  'consultant.released': 'De nouveau en recherche',
  'consultant.categorised': 'Vos catégories de métier',
  'dossier.generated': 'CV adapté à cette offre',
  'dossier.bullet_reverted': 'Une phrase remise à l’original',
  'candidature.created': 'Candidature créée',
  'candidature.status': 'Candidature : changement d’étape',
  'offer.scored': 'Comparée à votre profil',
  'offer.scored_on_demand': 'Comparée à votre profil, à la demande',
  'score.feedback': 'Votre avis sur ce score',
  'consultant.enrichment_facts_added': 'Vos réponses aux questions d’écart enregistrées',
  'courriers.coupes': 'Vous ne recevez plus les mails de Radar',
  'courriers.rythme': 'Vous avez changé le rythme de vos mails',
  'courriers.reactives': 'Vous recevez à nouveau les mails de Radar',
  'courriers.pause': 'Vous mettez les mails de Radar en pause',
  'courriers.pause_levee': 'Vous reprenez les mails de Radar',
  'compte.supprime': 'Vous supprimez votre compte',
  'courriers.regions': 'Vous avez changé vos régions',
  'courriers.reglages': 'Vous avez changé vos mails',
  'courriers.exclusions': 'Vous avez changé ce que vous refusez',
  'suggestion.ignoree': 'Vous avez écarté une suggestion',
  'courriers.connexion': 'Connexion depuis un mail de Radar',
  'candidature.relance_redigee': 'Relance rédigée pour vous',
  'employeurs.pont_refuse': 'Vous avez décliné le suivi d’un employeur',
  'courriers.adresse': 'Vous avez changé l’adresse de vos mails',
  'offer.rappel': 'Radar vous a rappelé cette offre',
}

const EVENT_RADAR_EN: Record<string, string> = {
  'consultant.imported': 'Profile created from your CV',
  'consultant.updated': 'Profile updated from your CV',
  'consultant.mise_en_page': 'Layout taken from your CV',
  'consultant.mise_en_page_refusee': 'Layout could not be taken from your CV',
  'consultant.edited': 'CV edited by hand',
  'consultant.archived': 'Profile archived',
  'consultant.unarchived': 'Profile reactivated',
  'consultant.placed': 'Employed',
  'consultant.released': 'Looking again',
  'consultant.categorised': 'Your occupation categories',
  'dossier.generated': 'CV tailored to this posting',
  'dossier.bullet_reverted': 'A bullet reverted to the original',
  'candidature.created': 'Application created',
  'candidature.status': 'Application: stage changed',
  'offer.scored': 'Compared to your profile',
  'offer.scored_on_demand': 'Compared to your profile, on demand',
  'score.feedback': 'Your feedback on this score',
  'consultant.enrichment_facts_added': 'Your answers to gap questions saved',
  'candidature.relance_redigee': 'Follow-up drafted for you',
  'employeurs.pont_refuse': 'You declined to follow an employer',
  'courriers.adresse': 'You changed your email address',
  'offer.rappel': 'Radar reminded you of this job',
}

const EVENT_RADAR_NL: Record<string, string> = {
  'consultant.imported': 'Profiel aangemaakt op basis van uw cv',
  'consultant.updated': 'Profiel bijgewerkt op basis van uw cv',
  'consultant.mise_en_page': 'Opmaak uit uw cv gehaald',
  'consultant.mise_en_page_refusee': 'Opmaak niet uit uw cv gehaald',
  'consultant.edited': 'Cv met de hand aangepast',
  'consultant.archived': 'Profiel gearchiveerd',
  'consultant.unarchived': 'Profiel opnieuw actief',
  'consultant.placed': 'Aan het werk',
  'consultant.released': 'Opnieuw op zoek',
  'consultant.categorised': 'Uw beroepscategorieën',
  'dossier.generated': 'Cv aangepast aan deze vacature',
  'dossier.bullet_reverted': 'Een zin teruggezet naar het origineel',
  'candidature.created': 'Sollicitatie aangemaakt',
  'candidature.status': 'Sollicitatie: andere stap',
  'offer.scored': 'Vergeleken met uw profiel',
  'offer.scored_on_demand': 'Vergeleken met uw profiel, op aanvraag',
  'score.feedback': 'Uw mening over deze score',
  'consultant.enrichment_facts_added': 'Uw antwoorden op de vragen over ontbrekende punten bewaard',
  'courriers.coupes': 'U ontvangt geen mails van Radar meer',
  'courriers.rythme': 'U hebt het ritme van uw mails gewijzigd',
  'courriers.reactives': 'U ontvangt opnieuw mails van Radar',
  'courriers.pause': 'U pauzeert de mails van Radar',
  'courriers.pause_levee': 'U hervat de mails van Radar',
  'compte.supprime': 'U verwijdert uw account',
  'courriers.regions': 'U hebt uw regio’s gewijzigd',
  'courriers.reglages': 'U hebt uw mails gewijzigd',
  'courriers.exclusions': 'U hebt gewijzigd wat u weigert',
  'suggestion.ignoree': 'U hebt een suggestie genegeerd',
  'courriers.connexion': 'Aangemeld via een mail van Radar',
  'candidature.relance_redigee': 'Opvolgmail voor u opgesteld',
  'employeurs.pont_refuse': 'U wilde een werkgever niet volgen',
  'courriers.adresse': 'U hebt het adres van uw mails gewijzigd',
  'offer.rappel': 'Radar heeft u aan deze vacature herinnerd',
}

const EVENT_CONSOLE_IT: Record<string, string> = {
  'offer.detected': 'Rilevata sulla fonte',
  'dossier.bullet_reverted': 'Una frase del dossier riportata all’originale',
  'source.added': 'Fonte aggiunta all’osservatorio',
  'source_request.created': 'Attivazione di un sito richiesta',
  'source_request.notified': 'Richiesta inviata per e-mail',
  'source_request.activated': 'Sito richiesto ora osservato',
  'source_request.activation_notified': 'Messa online comunicata al richiedente',
  'source_request.refused': 'Richiesta rifiutata',
  'source_request.refusal_notified': 'Rifiuto comunicato al richiedente',
  'source_request.renotified': 'Conferma di ricezione inviata di nuovo',
  'source.subscribed': 'Sito seguito da un account',
  'source.unsubscribed': 'Sito non più seguito da un account',
  'source.filtre': 'Filtro di un sito seguito cambiato',
  'source.elargie': 'Filtro spostato dalla configurazione di un sito ai suoi follower',
  'extension.token_created': 'Token dell’estensione creato',
  'feedback.sent': 'Feedback inviato dall’account',
  'courriers.coupes': 'E-mail di Radar disattivate',
  'courriers.rythme': 'Ritmo delle e-mail cambiato',
  'courriers.reactives': 'E-mail di Radar riattivate',
  'courriers.pause': 'E-mail di Radar in pausa',
  'courriers.pause_levee': 'Pausa delle e-mail terminata',
  'compte.supprime': 'Account eliminato',
  'essai.rappel': 'Promemoria di fine prova inviato',
  'courriers.regions': 'Regioni delle e-mail cambiate',
  'courriers.reglages': 'Impostazioni delle e-mail cambiate',
  'courriers.exclusions': 'Criteri di esclusione cambiati',
  'suggestion.ignoree': 'Suggerimento scartato',
  'courriers.connexion': 'Accesso dal link di un’e-mail',
  'candidature.relance_redigee': 'Sollecito redatto',
  'employeurs.pont_refuse': 'Proposta di seguire un datore di lavoro rifiutata',
  'courriers.adresse': 'Indirizzo di ricezione delle e-mail cambiato',
  'offer.rappel': 'Offerta “più tardi” ricordata per e-mail',
  'offer.score_failed': 'Valutazione non riuscita, da riprendere alla prossima elaborazione',
  'source.updated': 'Fonte corretta',
  'source.logo': 'Logo cambiato',
  'employeur.logo': 'Logo del datore di lavoro cambiato',
  'source.restored': 'Fonte rimessa nell’osservatorio',
  'settings.changed': 'Impostazione modificata',
  'run.completed': 'Elaborazione notturna terminata',
  'consultant.imported': 'Importato nel vivaio',
  'consultant.updated': 'Profilo aggiornato (CV reimportato)',
  'consultant.mise_en_page': 'Layout ripreso dal CV',
  'consultant.mise_en_page_refusee': 'Layout rifiutato',
  'consultant.edited': 'CV ritoccato a mano',
  'consultant.archived': 'Archiviato, storico conservato',
  'consultant.reset': 'Account azzerato, profilo archiviato',
  'consultant.caught_up': 'Valutato sulle offerte già presenti',
  'consultant.unarchived': 'Riattivato nel vivaio',
  'consultant.placed': 'Inserito in missione',
  'consultant.released': 'Di nuovo disponibile',
  'dossier.generated': 'Dossier generato',
  'offer.status': 'Decisione di selezione',
  'candidature.created': 'Profilo proposto su questa offerta',
  'candidature.status': 'Candidatura: cambio di fase',
  'candidature.archived': 'Candidatura ritirata',
  'candidature.restored': 'Candidatura rimessa in gioco',
  'offer.scored': 'Valutata automaticamente',
  'offer.scored_on_demand': 'Profilo valutato su richiesta',
  'offer.read': 'Letta',
  'offer.expired': 'Scaduta, sparita dal sito',
  'offer.relisted': 'Ripubblicata dal sito',
  'offer.categorised': 'Classificata in una categoria professionale',
  'consultant.categorised': 'Categorie professionali definite',
  'score.feedback': 'Parere dato su un punteggio',
  'consultant.enrichment_facts_added': 'Risposte alle domande sulle lacune salvate',
  'dossier.fragment_revised': 'Passaggio rivisto dall’IA',
  'dossier.edited': 'CV corretto a mano',
  'dossier.letter_generated': 'Lettera di presentazione generata',
  'dossier.letter_refined': 'Lettera di presentazione modificata',
  'dossier.letter_kept': 'Lettera mantenuta per la nuova versione del CV',
  'dossier.letter_angles': 'Taglio della lettera scelto',
  'candidature.entretien_prepare': 'Colloquio preparato',
  'offre.salaire_estime': 'Stipendio stimato',
  'source.error': 'Errore di lettura della fonte',
  'source.fallback': 'Ripiego usato per leggere la fonte',
  'source.archivage_suspendu': 'Archiviazione sospesa: troppe offerte sparite insieme',
  'source.archivage_force': 'Archiviazione forzata dopo verifica',
  'offer.description_missing': 'Descrizione assente o troppo breve',
  'offer.added': 'Aggiunta a mano',
}

const EVENT_RADAR_IT: Record<string, string> = {
  'consultant.imported': 'Profilo creato dal tuo CV',
  'consultant.updated': 'Profilo aggiornato dal tuo CV',
  'consultant.mise_en_page': 'Layout ripreso dal tuo CV',
  'consultant.mise_en_page_refusee': 'Layout non ricavato dal tuo CV',
  'consultant.edited': 'CV ritoccato a mano',
  'consultant.archived': 'Profilo archiviato',
  'consultant.unarchived': 'Profilo riattivato',
  'consultant.placed': 'Occupato',
  'consultant.released': 'Di nuovo in cerca',
  'consultant.categorised': 'Le tue categorie professionali',
  'dossier.generated': 'CV adattato a questa offerta',
  'dossier.bullet_reverted': 'Una frase riportata all’originale',
  'candidature.created': 'Candidatura creata',
  'candidature.status': 'Candidatura: cambio di fase',
  'offer.scored': 'Confrontata con il tuo profilo',
  'offer.scored_on_demand': 'Confrontata con il tuo profilo, su richiesta',
  'score.feedback': 'Il tuo parere su questo punteggio',
  'consultant.enrichment_facts_added': 'Le tue risposte alle domande sulle lacune salvate',
  'courriers.coupes': 'Non ricevi più le e-mail di Radar',
  'courriers.rythme': 'Hai cambiato il ritmo delle tue e-mail',
  'courriers.reactives': 'Ricevi di nuovo le e-mail di Radar',
  'courriers.pause': 'Metti in pausa le e-mail di Radar',
  'courriers.pause_levee': 'Riprendi le e-mail di Radar',
  'compte.supprime': 'Elimini il tuo account',
  'courriers.regions': 'Hai cambiato le tue regioni',
  'courriers.reglages': 'Hai cambiato le tue e-mail',
  'courriers.exclusions': 'Hai cambiato ciò che rifiuti',
  'suggestion.ignoree': 'Hai scartato un suggerimento',
  'courriers.connexion': 'Accesso da un’e-mail di Radar',
  'candidature.relance_redigee': 'Sollecito redatto per te',
  'employeurs.pont_refuse': 'Hai rifiutato di seguire un datore di lavoro',
  'courriers.adresse': 'Hai cambiato l’indirizzo delle tue e-mail',
  'offer.rappel': 'Radar ti ha ricordato questa offerta',
}

const EVENT_CONSOLE_ES: Record<string, string> = {
  'offer.detected': 'Detectada en la fuente',
  'dossier.bullet_reverted': 'Una frase del expediente restaurada al original',
  'source.added': 'Fuente añadida a la vigilancia',
  'source_request.created': 'Activación de un sitio solicitada',
  'source_request.notified': 'Solicitud enviada por correo',
  'source_request.activated': 'Sitio solicitado ahora vigilado',
  'source_request.activation_notified': 'Puesta en línea comunicada al solicitante',
  'source_request.refused': 'Solicitud rechazada',
  'source_request.refusal_notified': 'Rechazo comunicado al solicitante',
  'source_request.renotified': 'Acuse de recibo reenviado',
  'source.subscribed': 'Sitio seguido por una cuenta',
  'source.unsubscribed': 'Sitio dejado de seguir por una cuenta',
  'source.filtre': 'Filtro de un sitio seguido cambiado',
  'source.elargie': 'Filtro movido de la configuración de un sitio a sus seguidores',
  'extension.token_created': 'Token de la extensión creado',
  'feedback.sent': 'Opinión enviada por la cuenta',
  'courriers.coupes': 'Correos de Radar desactivados',
  'courriers.rythme': 'Ritmo de los correos cambiado',
  'courriers.reactives': 'Correos de Radar reactivados',
  'courriers.pause': 'Correos de Radar en pausa',
  'courriers.pause_levee': 'Pausa de los correos terminada',
  'compte.supprime': 'Cuenta eliminada',
  'essai.rappel': 'Recordatorio de fin de prueba enviado',
  'courriers.regions': 'Regiones de los correos cambiadas',
  'courriers.reglages': 'Ajustes de los correos cambiados',
  'courriers.exclusions': 'Criterios de exclusión cambiados',
  'suggestion.ignoree': 'Sugerencia descartada',
  'courriers.connexion': 'Inicio de sesión desde el enlace de un correo',
  'candidature.relance_redigee': 'Seguimiento redactado',
  'employeurs.pont_refuse': 'Sugerencia de seguir a un empleador rechazada',
  'courriers.adresse': 'Dirección de recepción de los correos cambiada',
  'offer.rappel': 'Oferta “más tarde” recordada por correo',
  'offer.score_failed': 'Evaluación imposible, a retomar en el próximo proceso',
  'source.updated': 'Fuente corregida',
  'source.logo': 'Logotipo cambiado',
  'employeur.logo': 'Logotipo del empleador cambiado',
  'source.restored': 'Fuente devuelta a la vigilancia',
  'settings.changed': 'Ajuste modificado',
  'run.completed': 'Proceso nocturno terminado',
  'consultant.imported': 'Importado a la cantera',
  'consultant.updated': 'Perfil actualizado (CV reimportado)',
  'consultant.mise_en_page': 'Diseño sacado del CV',
  'consultant.mise_en_page_refusee': 'Diseño rechazado',
  'consultant.edited': 'CV retocado a mano',
  'consultant.archived': 'Archivado, historial conservado',
  'consultant.reset': 'Cuenta restablecida, perfil archivado',
  'consultant.caught_up': 'Evaluado con las ofertas ya presentes',
  'consultant.unarchived': 'Reactivado en la cantera',
  'consultant.placed': 'Colocado en misión',
  'consultant.released': 'Disponible de nuevo',
  'dossier.generated': 'Expediente generado',
  'offer.status': 'Decisión de clasificación',
  'candidature.created': 'Perfil propuesto para esta oferta',
  'candidature.status': 'Candidatura: cambio de etapa',
  'candidature.archived': 'Candidatura retirada',
  'candidature.restored': 'Candidatura recuperada',
  'offer.scored': 'Evaluada automáticamente',
  'offer.scored_on_demand': 'Perfil evaluado a petición',
  'offer.read': 'Leída',
  'offer.expired': 'Caducada, desaparecida del sitio',
  'offer.relisted': 'Republicada por el sitio',
  'offer.categorised': 'Clasificada en una categoría profesional',
  'consultant.categorised': 'Categorías profesionales definidas',
  'score.feedback': 'Opinión dada sobre una puntuación',
  'consultant.enrichment_facts_added': 'Respuestas a las preguntas sobre carencias guardadas',
  'dossier.fragment_revised': 'Pasaje revisado por la IA',
  'dossier.edited': 'CV corregido a mano',
  'dossier.letter_generated': 'Carta de presentación generada',
  'dossier.letter_refined': 'Carta de presentación ajustada',
  'dossier.letter_kept': 'Carta conservada para la nueva versión del CV',
  'dossier.letter_angles': 'Enfoque de la carta elegido',
  'candidature.entretien_prepare': 'Entrevista preparada',
  'offre.salaire_estime': 'Salario estimado',
  'source.error': 'Error al leer la fuente',
  'source.fallback': 'Alternativa usada para leer la fuente',
  'source.archivage_suspendu': 'Archivado suspendido: demasiadas ofertas desaparecidas a la vez',
  'source.archivage_force': 'Archivado forzado tras comprobación',
  'offer.description_missing': 'Descripción ausente o demasiado corta',
  'offer.added': 'Añadida a mano',
}

const EVENT_RADAR_ES: Record<string, string> = {
  'consultant.imported': 'Perfil creado a partir de tu CV',
  'consultant.updated': 'Perfil actualizado a partir de tu CV',
  'consultant.mise_en_page': 'Diseño sacado de tu CV',
  'consultant.mise_en_page_refusee': 'Diseño no sacado de tu CV',
  'consultant.edited': 'CV retocado a mano',
  'consultant.archived': 'Perfil archivado',
  'consultant.unarchived': 'Perfil reactivado',
  'consultant.placed': 'Con empleo',
  'consultant.released': 'Buscando de nuevo',
  'consultant.categorised': 'Tus categorías profesionales',
  'dossier.generated': 'CV adaptado a esta oferta',
  'dossier.bullet_reverted': 'Una frase restaurada al original',
  'candidature.created': 'Candidatura creada',
  'candidature.status': 'Candidatura: cambio de etapa',
  'offer.scored': 'Comparada con tu perfil',
  'offer.scored_on_demand': 'Comparada con tu perfil, a petición',
  'score.feedback': 'Tu opinión sobre esta puntuación',
  'consultant.enrichment_facts_added': 'Tus respuestas a las preguntas sobre carencias guardadas',
  'courriers.coupes': 'Ya no recibes los correos de Radar',
  'courriers.rythme': 'Has cambiado el ritmo de tus correos',
  'courriers.reactives': 'Vuelves a recibir los correos de Radar',
  'courriers.pause': 'Pones en pausa los correos de Radar',
  'courriers.pause_levee': 'Reanudas los correos de Radar',
  'compte.supprime': 'Eliminas tu cuenta',
  'courriers.regions': 'Has cambiado tus regiones',
  'courriers.reglages': 'Has cambiado tus correos',
  'courriers.exclusions': 'Has cambiado lo que rechazas',
  'suggestion.ignoree': 'Has descartado una sugerencia',
  'courriers.connexion': 'Inicio de sesión desde un correo de Radar',
  'candidature.relance_redigee': 'Seguimiento redactado para ti',
  'employeurs.pont_refuse': 'Has rechazado seguir a un empleador',
  'courriers.adresse': 'Has cambiado la dirección de tus correos',
  'offer.rappel': 'Radar te ha recordado esta oferta',
}

const EVENT_CONSOLE: Record<Lang, Record<string, string>> = { fr: EVENT_CONSOLE_FR, en: EVENT_CONSOLE_EN, nl: EVENT_CONSOLE_NL, it: EVENT_CONSOLE_IT, es: EVENT_CONSOLE_ES }
const EVENT_RADAR: Record<Lang, Record<string, string>> = { fr: EVENT_RADAR_FR, en: EVENT_RADAR_EN, nl: EVENT_RADAR_NL, it: EVENT_RADAR_IT, es: EVENT_RADAR_ES }

export function eventLabels(formule: 'console' | 'radar', lang: Lang = 'fr'): Record<string, string> {
  const base = EVENT_CONSOLE[lang]
  if (formule !== 'radar') return base
  return { ...base, ...EVENT_RADAR[lang] }
}

/** Compat pour les appelants qui ne connaissent pas la formule ni la langue. */
export const EVENT_LABELS = EVENT_CONSOLE_FR
